<template>
	<div class="tcp-page tcp-steps">
		<section class="tcp-steps__wrap" v-if="!items.length && step < 4">
			<div class="tcp-bag__empty">
				<p>Your cart is empty.</p>
				<router-link to="/shop" class="tcp-btn">Continue Shopping</router-link>
			</div>
		</section>

		<section v-else class="tcp-steps__wrap" :class="{ 'is-thanks': step === 4 }">
			<div class="tcp-steps__layout">
				<div>
					<div class="tcp-steps__bar">
						<button type="button" :class="{ 'is-current': step === 1, 'is-done': step > 1 }" @click="go(1)">
							<span><i v-if="step >= 1" class="fa fa-check"></i></span>
							<small>Personal Info</small>
						</button>
						<i class="tcp-steps__line"></i>
						<button type="button" :class="{ 'is-current': step === 2, 'is-done': step > 2, 'is-wait': step < 2 }" @click="go(2)">
							<span><i v-if="step >= 2" class="fa fa-check"></i><template v-else>2</template></span>
							<small>Shipping</small>
						</button>
						<i class="tcp-steps__line"></i>
						<button type="button" :class="{ 'is-current': step === 3, 'is-done': step > 3, 'is-wait': step < 3 }" @click="go(3)">
							<span><i v-if="step >= 3" class="fa fa-check"></i><template v-else>3</template></span>
							<small>Payment</small>
						</button>
						<i class="tcp-steps__line"></i>
						<button type="button" :class="{ 'is-current': step === 4, 'is-wait': step < 4 }" @click="go(4)">
							<span><i v-if="step >= 4" class="fa fa-check"></i><template v-else>4</template></span>
							<small>Thank You</small>
						</button>
					</div>

					<div v-if="step === 1" class="tcp-steps__card">
						<h1>Enter Your Information</h1>
						<p class="tcp-steps__lead">Please provide your personal details to continue.</p>
						<div class="tcp-steps__grid">
							<label>First Name
								<input v-model="form.first_name" type="text" placeholder="John">
							</label>
							<label>Last Name
								<input v-model="form.last_name" type="text" placeholder="Doe">
							</label>
						</div>
						<label>Email
							<input v-model="form.email" type="email" placeholder="john@example.com">
						</label>
						<label>Phone Number
							<input v-model="form.phone" type="tel" placeholder="+1 (555) 123-4567">
						</label>
						<div class="tcp-steps__actions">
							<router-link to="/cart" class="tcp-btn tcp-btn--outline">Back</router-link>
							<button type="button" class="tcp-btn" :disabled="!personalReady" @click="step = 2">Continue to Shipping <i class="fa fa-arrow-right"></i></button>
						</div>
					</div>

					<div v-else-if="step === 2" class="tcp-steps__card">
						<h1>Shipping Address</h1>
						<p class="tcp-steps__lead">Enter your delivery address below.</p>
						<label>Street Address
							<input v-model="form.address" type="text" placeholder="123 Main Street, Apt 4B">
						</label>
						<div class="tcp-pick">
							<label>Country</label>
							<button type="button" @click="togglePick('country')">
								<span :class="{ 'is-placeholder': !form.country }">{{ countryLabel }}</span>
								<i class="fa fa-chevron-down"></i>
							</button>
							<div v-if="openPick === 'country'" class="tcp-pick__menu">
								<div class="tcp-pick__shade" @click="openPick = ''"></div>
								<div class="tcp-pick__panel">
									<input v-model="pickQuery" type="search" placeholder="Search...">
									<button type="button" v-for="country in filteredCountries" :key="country" @click="chooseCountry(country)">{{ country }}</button>
								</div>
							</div>
						</div>
						<div class="tcp-pick">
							<label>{{ form.country === 'Mexico' ? 'State / Province' : 'State' }}</label>
							<button type="button" :disabled="!form.country" @click="togglePick('state')">
								<span :class="{ 'is-placeholder': !form.state_id }">{{ stateLabel }}</span>
								<i class="fa fa-chevron-down"></i>
							</button>
							<p v-if="!form.country" class="tcp-pick__hint">Please select a country first</p>
							<div v-if="openPick === 'state'" class="tcp-pick__menu">
								<div class="tcp-pick__shade" @click="openPick = ''"></div>
								<div class="tcp-pick__panel">
									<input v-model="pickQuery" type="search" placeholder="Search...">
									<button type="button" v-for="state in filteredStates" :key="state.id" @click="chooseState(state)">{{ state.name }}</button>
									<p v-if="!filteredStates.length">No results found</p>
								</div>
							</div>
						</div>
						<div class="tcp-pick">
							<label>City</label>
							<button type="button" :disabled="!form.state_id" @click="togglePick('city')">
								<span :class="{ 'is-placeholder': !form.town_id }">{{ cityLabel }}</span>
								<i class="fa fa-chevron-down"></i>
							</button>
							<p v-if="!form.state_id" class="tcp-pick__hint">Please select a state first</p>
							<div v-if="openPick === 'city'" class="tcp-pick__menu">
								<div class="tcp-pick__shade" @click="openPick = ''"></div>
								<div class="tcp-pick__panel">
									<input v-model="pickQuery" type="search" placeholder="Search...">
									<button type="button" v-for="town in filteredTowns" :key="town.id" @click="chooseTown(town)">{{ town.name }}</button>
									<p v-if="!filteredTowns.length">No results found</p>
								</div>
							</div>
						</div>
						<label>{{ form.country === 'Mexico' ? 'Postal Code (CP)' : 'ZIP Code' }}
							<input v-model="form.postal_code" type="text" :maxlength="form.country === 'Mexico' ? 5 : 10" :placeholder="form.country === 'Mexico' ? '06600' : '10001'">
						</label>
						<p v-if="error" class="tcp-alert">{{ error }}</p>
						<div class="tcp-steps__actions">
							<button type="button" class="tcp-btn tcp-btn--outline" @click="step = 1">Back</button>
							<button type="button" class="tcp-btn" :disabled="!shippingReady" @click="step = 3">Continue to Payment <i class="fa fa-arrow-right"></i></button>
						</div>
					</div>

					<div v-else-if="step === 3" class="tcp-steps__card">
						<h1>Payment Method</h1>
						<p class="tcp-steps__lead">Enter your payment information below.</p>
						<img class="tcp-steps__cards" :src="cardsUrl" alt="Accepted payment methods: American Express, Discover, Mastercard, Visa">
						<label>Cardholder Name
							<input v-model="card.name" type="text" placeholder="John Doe" autocomplete="off">
						</label>
						<label>Card Number
							<input v-model="card.number" type="text" inputmode="numeric" maxlength="19" placeholder="4532 1234 5678 9010" autocomplete="off" @input="formatCard">
						</label>
						<div class="tcp-steps__grid">
							<label>Expiry Date
								<input v-model="card.expiry" type="text" maxlength="5" placeholder="MM/YY" autocomplete="off" @input="formatExpiry">
							</label>
							<label>CVV
								<input v-model="card.cvv" type="text" inputmode="numeric" maxlength="4" placeholder="123" autocomplete="off">
							</label>
						</div>
						<p v-if="error" class="tcp-alert">{{ error }}</p>
						<div class="tcp-steps__actions">
							<button type="button" class="tcp-btn tcp-btn--outline" @click="step = 2">Back</button>
							<button type="button" class="tcp-btn" :disabled="!paymentReady || placing" @click="placeOrder">
								{{ placing ? 'Placing order…' : 'Complete Purchase' }} <i class="fa fa-arrow-right"></i>
							</button>
						</div>
					</div>

					<div v-else class="tcp-steps__card tcp-steps__thanks">
						<span class="tcp-steps__check"><i class="fa fa-check"></i></span>
						<h1>Thank You!</h1>
						<p class="tcp-steps__lead">Your order has been successfully placed.</p>
						<div class="tcp-steps__number">
							<p>Order Number</p>
							<strong>{{ order ? order.code : '' }}</strong>
						</div>
						<p>We've sent you a confirmation email with your order details.</p>
						<p>Your order will be processed and shipped within 1-2 business days.</p>
						<div class="tcp-steps__actions tcp-steps__actions--center">
							<router-link to="/" class="tcp-btn"><i class="fa fa-home"></i> Return to Home</router-link>
							<button type="button" class="tcp-btn tcp-btn--outline" @click="printPage"><i class="fa fa-print"></i> Print Confirmation</button>
						</div>
					</div>
				</div>

				<aside v-if="step < 4" class="tcp-steps__summary">
					<h2>Order Summary</h2>
					<div class="tcp-steps__items">
						<div v-for="item in items" :key="item.id">
							<div>
								<p>{{ item.name }}</p>
								<small>{{ item.presentation ? item.presentation + ' · ' : '' }}x{{ item.quantity }}</small>
							</div>
							<strong>{{ money(item.price * item.quantity) }}</strong>
						</div>
					</div>
					<div class="tcp-summary__row"><span>Subtotal</span><strong>{{ money(subtotal) }}</strong></div>
					<div class="tcp-summary__row"><span>Shipping</span><strong>{{ shipping === 0 ? 'Free' : money(shipping) }}</strong></div>
					<div class="tcp-summary__row"><span>Tax</span><strong>{{ money(tax) }}</strong></div>
					<div class="tcp-summary__row tcp-summary__total"><span>Total</span><strong>{{ money(total) }}</strong></div>
					<div class="tcp-steps__delivery">
						<p><i class="fa fa-calendar-o"></i> Estimated Delivery</p>
						<span>{{ deliveryDate }}</span>
					</div>
					<ul class="tcp-steps__status">
						<li class="is-on"><i class="fa fa-check-circle"></i> Personal Info</li>
						<li class="is-on"><i class="fa fa-check-circle"></i> Shipping</li>
						<li :class="{ 'is-on': step === 3 }"><span>3</span> Payment</li>
					</ul>
				</aside>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	data() {
		return {
			step: 1,
			items: [],
			states: [],
			towns: [],
			openPick: '',
			pickQuery: '',
			placing: false,
			error: '',
			order: null,
			cardsUrl: tools.url('/public/images/payment-methods-cards.png'),
			countries: ['Mexico', 'United States'],
			form: {
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
				country: '',
				state_id: '',
				state_name: '',
				town_id: '',
				town_name: '',
				address: '',
				postal_code: ''
			},
			card: {
				name: '',
				number: '',
				expiry: '',
				cvv: ''
			}
		}
	},
	computed: {
		subtotal() {
			return this.items.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0)
		},
		shipping() {
			if (!this.items.length || this.subtotal > 100) return 0
			return 10
		},
		tax() {
			return Math.round(this.subtotal * 0.08 * 100) / 100
		},
		total() {
			return this.subtotal + this.shipping + this.tax
		},
		deliveryDate() {
			const date = new Date()
			date.setDate(date.getDate() + 5)
			return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
		},
		personalReady() {
			return this.form.first_name && this.form.last_name && this.form.email && this.form.phone
		},
		shippingReady() {
			return this.form.address && this.form.country && this.form.state_id && this.form.town_id && this.form.postal_code
		},
		paymentReady() {
			return this.card.name && this.card.number.replace(/\s/g, '').length >= 13 && this.card.expiry.length >= 4 && this.card.cvv.length >= 3
		},
		countryLabel() {
			return this.form.country || 'Select country...'
		},
		stateLabel() {
			return this.form.state_name || (this.form.country ? 'Select state...' : 'Select a country first')
		},
		cityLabel() {
			return this.form.town_name || (this.form.state_id ? 'Select city...' : 'Select a state first')
		},
		filteredCountries() {
			const term = this.pickQuery.trim().toLowerCase()
			return this.countries.filter(name => !term || name.toLowerCase().includes(term))
		},
		filteredStates() {
			const term = this.pickQuery.trim().toLowerCase()
			return this.states.filter(state => !term || state.name.toLowerCase().includes(term) || (state.acronym && state.acronym.toLowerCase().includes(term)))
		},
		filteredTowns() {
			const term = this.pickQuery.trim().toLowerCase()
			return this.towns.filter(town => !term || town.name.toLowerCase().includes(term))
		}
	},
	methods: {
		money(value) {
			return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
		},
		go(target) {
			if (target < this.step) this.step = target
		},
		togglePick(name) {
			if (name === 'state' && !this.form.country) return
			if (name === 'city' && !this.form.state_id) return
			this.pickQuery = ''
			this.openPick = this.openPick === name ? '' : name
		},
		chooseCountry(name) {
			this.form.country = name
			this.form.state_id = ''
			this.form.state_name = ''
			this.form.town_id = ''
			this.form.town_name = ''
			this.towns = []
			this.openPick = ''
			axios.get(tools.url('/api/store/states'), { params: { country: name } }).then((response) => {
				this.states = response.data || []
			}).catch(() => {})
		},
		chooseState(state) {
			this.form.state_id = state.id
			this.form.state_name = state.name
			this.form.town_id = ''
			this.form.town_name = ''
			this.openPick = ''
			axios.get(tools.url('/api/store/towns/' + state.id)).then((response) => {
				this.towns = response.data || []
			}).catch(() => {})
		},
		chooseTown(town) {
			this.form.town_id = town.id
			this.form.town_name = town.name
			this.openPick = ''
		},
		formatCard() {
			const digits = this.card.number.replace(/\D/g, '').slice(0, 16)
			this.card.number = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim()
		},
		formatExpiry() {
			const digits = this.card.expiry.replace(/\D/g, '').slice(0, 4)
			this.card.expiry = digits.length > 2 ? digits.slice(0, 2) + '/' + digits.slice(2) : digits
		},
		placeOrder() {
			this.error = ''
			this.placing = true
			axios.post(tools.url('/api/store/orders'), {
				customer_name: (this.form.first_name + ' ' + this.form.last_name).trim(),
				customer_email: this.form.email,
				customer_phone: this.form.phone,
				country: this.form.country,
				state_id: this.form.state_id,
				town_id: this.form.town_id,
				address: this.form.address,
				postal_code: this.form.postal_code,
				items: this.items.map(item => ({
					product_id: item.id,
					quantity: item.quantity
				}))
			}).then((response) => {
				this.order = response.data
				this.$cart.clear()
				sessionStorage.setItem('tcp_last_order', JSON.stringify(response.data))
				this.step = 4
				this.placing = false
			}).catch((error) => {
				const data = error.response && error.response.data
				if (data && data.errors) {
					const first = Object.values(data.errors)[0]
					this.error = Array.isArray(first) ? first[0] : (data.message || 'Could not place the order.')
				} else {
					this.error = (data && data.message) || 'Could not place the order.'
				}
				this.placing = false
			})
		},
		printPage() {
			if (!this.order || !this.order.pdf_url) return
			window.open(this.order.pdf_url, '_blank')
		}
	},
	mounted() {
		this.items = this.$cart.items()
	}
}
</script>
